import type { HiringRequest } from '../../services/api';
import {
  REQUEST_WORDS,
  requestLine,
  requestPill,
  requestReasonLine,
  requestTicksLine,
  requestWaitingTag,
} from './hiringText';

/**
 * A hiring request, drawn: one person asked for hiring to be raised, and a person who may
 * raise hiring confirms it or declines it. Nothing of it is in Recruitment until then.
 *
 * Three pieces, used by the hiring request window, the hiring panel and the Hiring tab:
 * what was asked (the facts box), where it stands (the pill, the server's sentence and
 * what stands in its way), and the panel's compact card.
 *
 * Read-only: nothing here calls the server. People are shown by employee code; a hiring
 * request holds no candidate, and none is drawn.
 */

const PILL = 'inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium';
const TAG = 'rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800';

/** A hiring request's pill: where it stands, in two or three words. */
export function HiringRequestPill({ request }: { request: Pick<HiringRequest, 'standing'> }) {
  const pill = requestPill(request);
  return <span className={`${PILL} ${pill.className}`}>{pill.text}</span>;
}

/** "Waiting 3 days", once the server says the request is old. Nothing before that. */
export function HiringRequestWaitingTag({ request }: { request: Pick<HiringRequest, 'old' | 'waitingDays' | 'standing'> }) {
  const tag = requestWaitingTag(request);
  return tag ? <span className={TAG}>{tag}</span> : null;
}

/** What was asked: by whom and when, under which budget, why, what they ticked, and their note. */
export function HiringRequestFacts({ request }: { request: HiringRequest }) {
  const ticks = requestTicksLine(request.ticks);
  return (
    <section aria-label={REQUEST_WORDS.factsLabel} className="space-y-1 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-medium text-gray-900">{REQUEST_WORDS.askedBy(request)}</p>
        <HiringRequestWaitingTag request={request} />
      </div>
      <p>{REQUEST_WORDS.budget(request.budget?.name ?? '')}</p>
      <p>{REQUEST_WORDS.reason(request)}</p>
      {ticks && <p>{ticks}</p>}
      {request.note && <p className="italic text-gray-600">“{request.note}”</p>}
    </section>
  );
}

/**
 * Where a hiring request stands: its pill, the server's own sentence and, while it waits,
 * what ChannelPlex's own tables say stands in its way. The fresh review is the judge of
 * that; these lines only say it early.
 */
export function HiringRequestStanding({ request }: { request: HiringRequest }) {
  const notes = request.standing === 'waiting' ? request.notes ?? [] : [];
  return (
    <div role="group" aria-label={REQUEST_WORDS.standingLabel} className="space-y-2">
      {/* On a phone the sentence has the full width under the pill, not a narrow column beside it. */}
      <div className="flex flex-wrap items-start gap-2">
        <HiringRequestPill request={request} />
        <p className="w-full text-sm text-gray-800 sm:w-auto sm:min-w-0 sm:flex-1">{requestLine(request)}</p>
      </div>
      {notes.length > 0 && (
        <ul aria-label={REQUEST_WORDS.notesLabel} className="space-y-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The hiring panel's card for a request that is waiting: above the opening cards, since
 * it is the thing a person is being asked to decide.
 */
export function HiringRequestCard({ request, onOpen }: { request: HiringRequest; onOpen?: () => void }) {
  const notes = request.notes ?? [];
  return (
    <section aria-label={REQUEST_WORDS.rowLine} className="space-y-2 rounded-lg border border-amber-200 bg-amber-50/40 p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h4 className="text-sm font-semibold text-gray-900">{REQUEST_WORDS.rowLine}</h4>
        <HiringRequestWaitingTag request={request} />
      </div>
      <p className="text-sm text-gray-800">{[request.budget?.name, requestReasonLine(request)].filter(Boolean).join(' · ')}</p>
      <p className="text-xs text-gray-600">{REQUEST_WORDS.askedBy(request)}</p>
      <p className="text-sm text-gray-800">{requestLine(request)}</p>
      {notes.length > 0 && (
        <ul aria-label={REQUEST_WORDS.notesLabel} className="space-y-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      )}
      {onOpen && (
        <div className="pt-1">
          <button
            type="button"
            onClick={onOpen}
            className="rounded-full border border-primary-200 bg-white px-3 py-1 text-xs font-semibold text-primary-700 transition-colors hover:bg-primary-50"
          >
            {REQUEST_WORDS.open}
          </button>
        </div>
      )}
    </section>
  );
}

export default HiringRequestCard;
